import React, { useEffect, useRef, useState } from 'react';
import {
    Box, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, CircularProgress, Chip, Grid,
    Pagination, IconButton, Button
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import HandshakeIcon from '@mui/icons-material/Handshake';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Tooltip } from '@mui/material';
import { paginationStyle } from '../../../theme/formStyles';
import SearchBox from '../../Common/SearchBox';
import Filter from '../../Common/Filter';

// Helper functions
const formatCurrency = (value) => {
    if (!value && value !== 0) return '-';
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
    }).format(value);
};

const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return '-';
        return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
        return '-';
    }
};

const StatusChip = ({ status }) => {
    const colorMap = {
        'open': '#2196f3',
        'partially_invoiced': '#ff9800',
        'invoiced': '#4caf50',
        'closed': '#9e9e9e',
        'void': '#f44336',
        'overdue': '#f44336',
        'draft': '#607d8b',
    };
    const color = colorMap[status?.toLowerCase()] || '#9e9e9e';
    return (
        <Chip
            label={status?.replace(/_/g, ' ') || '-'}
            size="small"
            sx={{
                backgroundColor: color + '20',
                color: color,
                fontWeight: 600,
                fontSize: '11px',
                textTransform: 'capitalize',
            }}
        />
    );
};

const itemsPerPage = 10;

// Sticky column widths and cumulative left offsets
const stickyWidths = [100, 100, 130, 150, 150];
const stickyLefts = stickyWidths.map((_, i) => stickyWidths.slice(0, i).reduce((a, b) => a + b, 0));

const stickyStyle = (colIndex, extra = {}) => ({
    position: 'sticky',
    left: stickyLefts[colIndex],
    minWidth: stickyWidths[colIndex],
    maxWidth: stickyWidths[colIndex],
    backgroundColor: extra.backgroundColor || '#fff',
    zIndex: 2,
    wordBreak: 'break-word',
    ...extra,
});

const stickyHeadStyle = (colIndex) => ({
    position: 'sticky',
    left: stickyLefts[colIndex],
    top: 0,
    minWidth: stickyWidths[colIndex],
    maxWidth: stickyWidths[colIndex],
    backgroundColor: '#fff',
    zIndex: 5,
    fontSize: '14px',
    fontWeight: 'bold',
    borderBottom: '1px solid #333',
});

export default function MasterData({
    data,
    loading,
    totals,
    totalClients,
    totalClientGroups,
    totalFacilities,
    totalSalesOrders,
    queryParams,
    handleSearch,
    setShowFilterDialog,
    handleRefreshCache,
    refreshing,
    handleDownload,
    downloading,
    showFilterDialog,
    updateQueryParams,
    setIsSearchOrFilterApplied,
    handleTabSwitch,
    bdFilterOptions,
    currentPage,
    totalPages,
    handlePageChange
}) {
    const tableContainerRef = useRef(null);
    const [showScrollIndicator, setShowScrollIndicator] = useState(false);
    const [poFromDate, setPoFromDate] = useState(queryParams.get('poFromDate') || '');
    const [poToDate, setPoToDate] = useState(queryParams.get('poToDate') || '');
    const [poExpiryFromDate, setPoExpiryFromDate] = useState(queryParams.get('poExpiryFromDate') || '');
    const [poExpiryToDate, setPoExpiryToDate] = useState(queryParams.get('poExpiryToDate') || '');
    const [nextInvoiceFromDate, setNextInvoiceFromDate] = useState(queryParams.get('nextInvoiceFromDate') || '');
    const [nextInvoiceToDate, setNextInvoiceToDate] = useState(queryParams.get('nextInvoiceToDate') || '');
    const [poDateError, setPoDateError] = useState('');
    const [poExpiryDateError, setPoExpiryDateError] = useState('');
    const [nextInvoiceDateError, setNextInvoiceDateError] = useState('');

    const validateToDate = (fromDate, toDate) =>
        fromDate && toDate && toDate < fromDate
            ? "'To Date' must be equal to or later than 'From Date'"
            : '';

    const handleApplyDateFilter = () => {
        if (poDateError || poExpiryDateError || nextInvoiceDateError) return;
        setIsSearchOrFilterApplied(true);
        updateQueryParams({ poFromDate, poToDate, poExpiryFromDate, poExpiryToDate, nextInvoiceFromDate, nextInvoiceToDate, page: 1 });
    };

    const handleClearDateFilter = () => {
        setPoFromDate('');
        setPoToDate('');
        setPoExpiryFromDate('');
        setPoExpiryToDate('');
        setNextInvoiceFromDate('');
        setNextInvoiceToDate('');
        setPoDateError('');
        setPoExpiryDateError('');
        setNextInvoiceDateError('');
        setIsSearchOrFilterApplied(true);
        updateQueryParams({ poFromDate: '', poToDate: '', poExpiryFromDate: '', poExpiryToDate: '', nextInvoiceFromDate: '', nextInvoiceToDate: '', page: 1 });
    };

    const checkScroll = () => {
        const container = tableContainerRef.current;
        if (container) {
            const hasScroll = container.scrollWidth > container.clientWidth;
            const isScrolled = container.scrollLeft > 0;
            setShowScrollIndicator(hasScroll && isScrolled);
        }
    };

    useEffect(() => {
        checkScroll();
        window.addEventListener('resize', checkScroll);
        return () => window.removeEventListener('resize', checkScroll);
    }, [data]);

    useEffect(() => {
        setPoFromDate(queryParams.get('poFromDate') || '');
        setPoToDate(queryParams.get('poToDate') || '');
        setPoExpiryFromDate(queryParams.get('poExpiryFromDate') || '');
        setPoExpiryToDate(queryParams.get('poExpiryToDate') || '');
        setNextInvoiceFromDate(queryParams.get('nextInvoiceFromDate') || '');
        setNextInvoiceToDate(queryParams.get('nextInvoiceToDate') || '');
    }, [queryParams]);

    // Group data by client_group for rowSpan rendering
    const groupedByClientGroup = {};
    data.forEach(row => {
        const key = `${row.client_type}||${row.segment}||${row.client_group}`;
        if (!groupedByClientGroup[key]) {
            groupedByClientGroup[key] = {
                client_type: row.client_type,
                segment: row.segment,
                client_group: row.client_group,
                company_name: row.company_name,
                facility_name: row.facility_name,
                owner: row.owner || [],
                customer_status: row.customer_status || '',
                orders: [],
            };
        }
        groupedByClientGroup[key].orders.push(row);
    });

    const clientGroupGroups = Object.values(groupedByClientGroup);

    // Compute facility-level totals
    const computeFacilityTotals = (orders) => ({
        totalContractValue: orders.reduce((sum, o) => sum + (o.contract_value || 0), 0),
        totalInvoiced: orders.reduce((sum, o) => sum + (o.total_invoiced_amount || 0), 0),
        totalOutstanding: orders.reduce((sum, o) => sum + (o.outstanding || 0), 0),
    });

    const stickyHeaders = ['Client Type', 'Client Group', 'Company Name', 'Facility', 'Segment'];
    const scrollHeaders = [
        'BD Owner', 'Project Owner', 'SO/PO No',
        'Customer Status', 'Status', 'Contract Type', 'Billing Cycle', 'PO Date', 'PO Start Date', 'PO Expiry Date',
        'Contract Value', 'Total Contract Value',
        'Invoiced Date', 'Invoiced', 'Total Invoiced',
        'PO to Invoice Date', 'Outstanding ₹', 'Invoice Numbers', 'Aging (Days)',
        'Total Outstanding', 'Payment Realization Date', 'Next Invoice Date'
    ];

    let tableContent = null;

    if (loading) {
        tableContent = (
            <TableRow>
                <TableCell colSpan={stickyHeaders.length + scrollHeaders.length} align="center">
                    <CircularProgress sx={{ color: '#008080' }} />
                </TableCell>
            </TableRow>
        );
    } else if (clientGroupGroups.length === 0) {
        tableContent = (
            <TableRow>
                <TableCell colSpan={stickyHeaders.length + scrollHeaders.length} align="left">
                    <h5 className="w3-text-grey">No Data Found</h5>
                </TableCell>
            </TableRow>
        );
    } else {
        tableContent = clientGroupGroups.map((clientGroup, groupIndex) => {
            const orders = clientGroup.orders;
            const totals = computeFacilityTotals(orders);
            const isLastGroup = groupIndex === clientGroupGroups.length - 1;

            return orders.map((row, index) => {
                const isLast = index === orders.length - 1;
                const nextGroup = groupIndex < clientGroupGroups.length - 1 ? clientGroupGroups[groupIndex + 1] : null;
                const isClientChange = nextGroup && nextGroup.client_group !== clientGroup.client_group;

                const mergedBorder = (!isLastGroup && isClientChange) ? '2px solid #bdbdbd' : 'none';
                const soBorder = (!isLastGroup) ? (isClientChange ? '2px solid #bdbdbd' : '1px solid #e0e0e0') : 'none';
                const innerBorder = isLast ? soBorder : 'none';

                return (
                    <TableRow
                        key={`${clientGroup.client_group}-${index}`}
                        sx={{}}
                    >
                        {index === 0 && (
                            <>
                                <TableCell style={{ ...stickyStyle(0), borderBottom: mergedBorder, fontWeight: 500 }} rowSpan={orders.length}>
                                    {clientGroup.client_type || '-'}
                                </TableCell>
                                <TableCell style={{ ...stickyStyle(1), borderBottom: mergedBorder, fontWeight: 500 }} rowSpan={orders.length}>
                                    {clientGroup.client_group || '-'}
                                </TableCell>
                                <TableCell style={{ ...stickyStyle(2), borderBottom: mergedBorder, fontWeight: 500 }} rowSpan={orders.length}>
                                    {clientGroup.company_name || '-'}
                                </TableCell>
                            </>
                        )}

                        <TableCell style={{ ...stickyStyle(3), borderBottom: innerBorder }}>{row.facility_name || '-'}</TableCell>
                        
                        {index === 0 && (
                            <TableCell style={{ ...stickyStyle(4), borderBottom: mergedBorder, fontWeight: 500 }} rowSpan={orders.length}>
                                {clientGroup.segment || '-'}
                            </TableCell>
                        )}

                        <TableCell style={{ borderBottom: innerBorder }}>{row.bd_owner?.name || '-'}</TableCell>
                        <TableCell style={{ borderBottom: innerBorder }}>{row.project_owner?.name || '-'}</TableCell>
                        <TableCell style={{ borderBottom: innerBorder }}>{row.so_po_no || '-'}</TableCell>
                        {index === 0 && (
                            <TableCell style={{ borderBottom: soBorder, fontWeight: 500 }} rowSpan={orders.length}>
                                <span style={{ color: clientGroup.customer_status === 'Active' ? '#2e7d32' : '#d32f2f', fontWeight: 600 }}>
                                    {clientGroup.customer_status || '-'}
                                </span>
                            </TableCell>
                        )}
                        <TableCell style={{ borderBottom: innerBorder }}><StatusChip status={row.status} /></TableCell>
                        <TableCell style={{ borderBottom: innerBorder }}>{row.contract_type || '-'}</TableCell>
                        <TableCell style={{ borderBottom: innerBorder }}>{row.billing_cycle || '-'}</TableCell>
                        <TableCell style={{ borderBottom: innerBorder }}>{formatDate(row.po_date)}</TableCell>
                        <TableCell style={{ borderBottom: innerBorder }}>{formatDate(row.po_start_date)}</TableCell>
                        <TableCell style={{ borderBottom: innerBorder }}>{formatDate(row.po_expiry_date)}</TableCell>
                        <TableCell style={{ borderBottom: innerBorder }}>{formatCurrency(row.contract_value)}</TableCell>

                        {index === 0 && (
                            <TableCell style={{ borderBottom: soBorder, fontWeight: 600, color: '#00695c' }} rowSpan={orders.length}>
                                {formatCurrency(totals.totalContractValue)}
                            </TableCell>
                        )}

                        <TableCell style={{ borderBottom: innerBorder }}>{formatDate(row.last_invoiced_date)}</TableCell>
                        <TableCell style={{ borderBottom: innerBorder }}>{formatCurrency(row.total_invoiced_amount)}</TableCell>

                        {index === 0 && (
                            <TableCell style={{ borderBottom: soBorder, fontWeight: 600, color: '#00695c' }} rowSpan={orders.length}>
                                {formatCurrency(totals.totalInvoiced)}
                            </TableCell>
                        )}

                        <TableCell style={{ borderBottom: innerBorder }}>{row.po_to_invoice_days !== null ? `${row.po_to_invoice_days} days` : '-'}</TableCell>
                        <TableCell style={{ borderBottom: innerBorder }}>{formatCurrency(row.outstanding)}</TableCell>
                        <TableCell style={{ borderBottom: innerBorder, fontSize: '12px', maxWidth: 200, wordBreak: 'break-word' }}>{row.outstanding_invoice_numbers || '-'}</TableCell>
                        <TableCell style={{ borderBottom: innerBorder }}>
                            <span style={{ color: row.aging_days > 90 ? '#f44336' : row.aging_days > 30 ? '#ff9800' : '#333' }}>
                                {row.aging_days || 0}
                            </span>
                        </TableCell>

                        {index === 0 && (
                            <TableCell style={{ borderBottom: soBorder, fontWeight: 600, color: totals.totalOutstanding > 0 ? '#e53935' : '#00695c' }} rowSpan={orders.length}>
                                {formatCurrency(totals.totalOutstanding)}
                            </TableCell>
                        )}

                        <TableCell style={{ borderBottom: innerBorder }}>{formatDate(row.payment_realization_date)}</TableCell>
                        <TableCell style={{ borderBottom: innerBorder }}>
                            {row.next_invoice_date && row.po_expiry_date && row.next_invoice_date > row.po_expiry_date
                                ? '-'
                                : formatDate(row.next_invoice_date)}
                        </TableCell>
                    </TableRow>
                );
            });
        });
    }
    return (
        <>
            {/* Search and Action Buttons — full-width row, right-aligned */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap', gap: 1, pt: 2, pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <h6 style={{ fontSize: '15px', margin: 0, whiteSpace: 'nowrap', paddingRight: '5px' }}>Search By:</h6>
                    <SearchBox
                        value={queryParams.get('search') || ''}
                        placeholder='SO/PO/Invoice Number'
                        search={handleSearch}
                    />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton onClick={() => setShowFilterDialog(true)} title="Apply Filter">
                        <FilterListIcon />
                    </IconButton>
                    <Tooltip title="Refresh data from Zoho">
                        <span>
                            <IconButton onClick={handleRefreshCache} disabled={refreshing}>
                                {refreshing ? <CircularProgress size={20} sx={{ color: '#008080' }} /> : <RefreshIcon />}
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Download">
                        <span>
                            <IconButton onClick={handleDownload} disabled={downloading}>
                                {downloading ? <CircularProgress size={20} sx={{ color: '#008080' }} /> : <DownloadIcon />}
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

            {/* Row 1: Amount cards */}
            <Grid container spacing={2} pb={2} alignItems="flex-start">
                <Grid item xs={12} sm={4}>
                    <Box sx={{ fontWeight: 600, color: 'white', background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', padding: '12px 16px', borderRadius: '8px', minWidth: '200px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', position: 'relative', overflow: 'hidden' }}>
                        <Box sx={{ position: 'absolute', right: -15, top: -15, opacity: 0.15 }}>
                            <HandshakeIcon sx={{ fontSize: 100 }} />
                        </Box>
                        <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '4px', position: 'relative', zIndex: 1 }}>Total Contract Value</div>
                        <div style={{ fontSize: '20px', fontWeight: 700, position: 'relative', zIndex: 1 }}>
                            {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : formatCurrency(totals.totalContractValue)}
                        </div>
                    </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Box sx={{ fontWeight: 600, color: 'white', background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', padding: '12px 16px', borderRadius: '8px', minWidth: '200px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', position: 'relative', overflow: 'hidden' }}>
                        <Box sx={{ position: 'absolute', right: -15, top: -15, opacity: 0.15 }}>
                            <ReceiptLongIcon sx={{ fontSize: 100 }} />
                        </Box>
                        <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '4px', position: 'relative', zIndex: 1 }}>Total Invoiced Value</div>
                        <div style={{ fontSize: '20px', fontWeight: 700, position: 'relative', zIndex: 1 }}>
                            {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : formatCurrency(totals.totalInvoiced)}
                        </div>
                    </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Box sx={{ fontWeight: 600, color: 'white', background: 'linear-gradient(135deg, #c62828 0%, #d32f2f 100%)', padding: '12px 16px', borderRadius: '8px', minWidth: '200px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', position: 'relative', overflow: 'hidden' }}>
                        <Box sx={{ position: 'absolute', right: -15, top: -15, opacity: 0.15 }}>
                            <WarningAmberIcon sx={{ fontSize: 100 }} />
                        </Box>
                        <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '4px', position: 'relative', zIndex: 1 }}>Total Overdue Amount</div>
                        <div style={{ fontSize: '20px', fontWeight: 700, position: 'relative', zIndex: 1 }}>
                            {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : formatCurrency(totals.totalOutstanding)}
                        </div>
                    </Box>
                </Grid>
            </Grid>

            {/* Row 2: Count stats */}
            <Grid container spacing={2} pb={1}>
                <Grid item xs={12} sm={4}>
                    <Box sx={{ fontWeight: 600, color: 'black', backgroundColor: 'white', padding: '10px', borderRadius: '4px' }}>
                        Total Clients: &nbsp;
                        <span style={{ fontWeight: 600, color: '#e68a00', fontSize: '18px' }}>
                            {loading ? <CircularProgress size={20} color="primary" /> : totalClients}
                        </span>
                    </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Box sx={{ fontWeight: 600, color: 'black', backgroundColor: 'white', padding: '10px', borderRadius: '4px' }}>
                        Total Facilities: &nbsp;
                        <span style={{ fontWeight: 600, color: '#e68a00', fontSize: '18px' }}>
                            {loading ? <CircularProgress size={20} color="primary" /> : totalFacilities}
                        </span>
                    </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Box sx={{ fontWeight: 600, color: 'black', backgroundColor: 'white', padding: '10px', borderRadius: '4px' }}>
                        Total Sales Orders: &nbsp;
                        <span style={{ fontWeight: 600, color: '#e68a00', fontSize: '18px' }}>
                            {loading ? <CircularProgress size={20} color="primary" /> : totalSalesOrders}
                        </span>
                    </Box>
                </Grid>
            </Grid>

            {/* Row 3: Date filters — full width below Total Sales Orders */}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', pb: 2 }}>

                    {/* PO Date */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <h6 style={{ fontSize: '14px', margin: 0, minWidth: '62px', whiteSpace: 'nowrap', paddingTop: '8px' }}>PO Date:</h6>
                        <DatePicker
                            format="DD/MM/YYYY"
                            value={poFromDate ? dayjs(poFromDate) : null}
                            onChange={(newValue) => {
                                const formatted = newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : '';
                                setPoFromDate(formatted);
                                setPoDateError(validateToDate(formatted, poToDate));
                            }}
                            slotProps={{
                                textField: { size: 'small', sx: { width: 130, '& .MuiInputBase-root': { paddingRight: '4px', backgroundColor: 'white', borderRadius: '4px' }, '&& input.MuiInputBase-input': { minWidth: '70px !important', width: '70px !important', padding: '8.5px 8px' }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#008080' } }, inputProps: { style: { fontSize: 13 } } },
                                day: { sx: { '&.Mui-selected': { backgroundColor: '#008080 !important' }, '&.Mui-selected:hover': { backgroundColor: '#006666 !important' } } },
                                actionBar: { sx: { '& .MuiButton-root': { color: '#008080' } } },
                            }}
                        />
                        <span style={{ fontSize: '14px', paddingTop: '8px' }}>to</span>
                        <Box>
                            <DatePicker
                                format="DD/MM/YYYY"
                                value={poToDate ? dayjs(poToDate) : null}
                                minDate={poFromDate ? dayjs(poFromDate) : undefined}
                                onChange={(newValue) => {
                                    const formatted = newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : '';
                                    setPoToDate(formatted);
                                    setPoDateError(validateToDate(poFromDate, formatted));
                                }}
                                slotProps={{
                                    textField: { size: 'small', error: !!poDateError, sx: { width: 130, '& .MuiInputBase-root': { paddingRight: '4px', backgroundColor: 'white', borderRadius: '4px' }, '&& input.MuiInputBase-input': { minWidth: '70px !important', width: '70px !important', padding: '8.5px 8px' }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#008080' } }, inputProps: { style: { fontSize: 13 } } },
                                    day: { sx: { '&.Mui-selected': { backgroundColor: '#008080 !important' }, '&.Mui-selected:hover': { backgroundColor: '#006666 !important' } } },
                                    actionBar: { sx: { '& .MuiButton-root': { color: '#008080' } } },
                                }}
                            />
                            {poDateError && <div style={{ color: '#d32f2f', fontSize: '11px', marginTop: '2px', maxWidth: 130 }}>{poDateError}</div>}
                        </Box>
                    </Box>

                    {/* PO Expiry Date */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <h6 style={{ fontSize: '14px', margin: 0, minWidth: '100px', whiteSpace: 'nowrap', paddingTop: '8px' }}>PO Expiry Date:</h6>
                        <DatePicker
                            format="DD/MM/YYYY"
                            value={poExpiryFromDate ? dayjs(poExpiryFromDate) : null}
                            onChange={(newValue) => {
                                const formatted = newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : '';
                                setPoExpiryFromDate(formatted);
                                setPoExpiryDateError(validateToDate(formatted, poExpiryToDate));
                            }}
                            slotProps={{
                                textField: { size: 'small', sx: { width: 130, '& .MuiInputBase-root': { paddingRight: '4px', backgroundColor: 'white', borderRadius: '4px' }, '&& input.MuiInputBase-input': { minWidth: '70px !important', width: '70px !important', padding: '8.5px 8px' }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#008080' } }, inputProps: { style: { fontSize: 13 } } },
                                day: { sx: { '&.Mui-selected': { backgroundColor: '#008080 !important' }, '&.Mui-selected:hover': { backgroundColor: '#006666 !important' } } },
                                actionBar: { sx: { '& .MuiButton-root': { color: '#008080' } } },
                            }}
                        />
                        <span style={{ fontSize: '14px', paddingTop: '8px' }}>to</span>
                        <Box>
                            <DatePicker
                                format="DD/MM/YYYY"
                                value={poExpiryToDate ? dayjs(poExpiryToDate) : null}
                                minDate={poExpiryFromDate ? dayjs(poExpiryFromDate) : undefined}
                                onChange={(newValue) => {
                                    const formatted = newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : '';
                                    setPoExpiryToDate(formatted);
                                    setPoExpiryDateError(validateToDate(poExpiryFromDate, formatted));
                                }}
                                slotProps={{
                                    textField: { size: 'small', error: !!poExpiryDateError, sx: { width: 130, '& .MuiInputBase-root': { paddingRight: '4px', backgroundColor: 'white', borderRadius: '4px' }, '&& input.MuiInputBase-input': { minWidth: '70px !important', width: '70px !important', padding: '8.5px 8px' }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#008080' } }, inputProps: { style: { fontSize: 13 } } },
                                    day: { sx: { '&.Mui-selected': { backgroundColor: '#008080 !important' }, '&.Mui-selected:hover': { backgroundColor: '#006666 !important' } } },
                                    actionBar: { sx: { '& .MuiButton-root': { color: '#008080' } } },
                                }}
                            />
                            {poExpiryDateError && <div style={{ color: '#d32f2f', fontSize: '11px', marginTop: '2px', maxWidth: 130 }}>{poExpiryDateError}</div>}
                        </Box>
                    </Box>

                    {/* Next Invoice Date */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <h6 style={{ fontSize: '14px', margin: 0, minWidth: '115px', whiteSpace: 'nowrap', paddingTop: '8px' }}>Next Invoice Date:</h6>
                        <DatePicker
                            format="DD/MM/YYYY"
                            value={nextInvoiceFromDate ? dayjs(nextInvoiceFromDate) : null}
                            onChange={(newValue) => {
                                const formatted = newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : '';
                                setNextInvoiceFromDate(formatted);
                                setNextInvoiceDateError(validateToDate(formatted, nextInvoiceToDate));
                            }}
                            slotProps={{
                                textField: { size: 'small', sx: { width: 130, '& .MuiInputBase-root': { paddingRight: '4px', backgroundColor: 'white', borderRadius: '4px' }, '&& input.MuiInputBase-input': { minWidth: '70px !important', width: '70px !important', padding: '8.5px 8px' }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#008080' } }, inputProps: { style: { fontSize: 13 } } },
                                day: { sx: { '&.Mui-selected': { backgroundColor: '#008080 !important' }, '&.Mui-selected:hover': { backgroundColor: '#006666 !important' } } },
                                actionBar: { sx: { '& .MuiButton-root': { color: '#008080' } } },
                            }}
                        />
                        <span style={{ fontSize: '14px', paddingTop: '8px' }}>to</span>
                        <Box>
                            <DatePicker
                                format="DD/MM/YYYY"
                                value={nextInvoiceToDate ? dayjs(nextInvoiceToDate) : null}
                                minDate={nextInvoiceFromDate ? dayjs(nextInvoiceFromDate) : undefined}
                                onChange={(newValue) => {
                                    const formatted = newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : '';
                                    setNextInvoiceToDate(formatted);
                                    setNextInvoiceDateError(validateToDate(nextInvoiceFromDate, formatted));
                                }}
                                slotProps={{
                                    textField: { size: 'small', error: !!nextInvoiceDateError, sx: { width: 130, '& .MuiInputBase-root': { paddingRight: '4px', backgroundColor: 'white', borderRadius: '4px' }, '&& input.MuiInputBase-input': { minWidth: '70px !important', width: '70px !important', padding: '8.5px 8px' }, '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#008080' } }, inputProps: { style: { fontSize: 13 } } },
                                    day: { sx: { '&.Mui-selected': { backgroundColor: '#008080 !important' }, '&.Mui-selected:hover': { backgroundColor: '#006666 !important' } } },
                                    actionBar: { sx: { '& .MuiButton-root': { color: '#008080' } } },
                                }}
                            />
                            {nextInvoiceDateError && <div style={{ color: '#d32f2f', fontSize: '11px', marginTop: '2px', maxWidth: 130 }}>{nextInvoiceDateError}</div>}
                        </Box>
                    </Box>

                    {/* Apply / Clear */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleApplyDateFilter}
                            disabled={!!(poDateError || poExpiryDateError || nextInvoiceDateError)}
                            sx={{ minWidth: '60px', textTransform: 'none', backgroundColor: '#008080', '&:hover': { backgroundColor: '#006666' } }}
                        >
                            Apply
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleClearDateFilter}
                            disabled={!poFromDate && !poToDate && !poExpiryFromDate && !poExpiryToDate && !nextInvoiceFromDate && !nextInvoiceToDate}
                            sx={{
                                minWidth: '60px',
                                textTransform: 'none',
                                color: (poFromDate || poToDate || poExpiryFromDate || poExpiryToDate || nextInvoiceFromDate || nextInvoiceToDate) ? '#008080' : undefined,
                                borderColor: (poFromDate || poToDate || poExpiryFromDate || poExpiryToDate || nextInvoiceFromDate || nextInvoiceToDate) ? '#008080' : undefined,
                            }}
                        >
                            Clear
                        </Button>
                    </Box>

                </Box>
            </LocalizationProvider>

            <Filter
                filterArea="BusinessDashboard"
                showFilterDialog={showFilterDialog}
                setShowFilterDialog={setShowFilterDialog}
                updateQueryParams={updateQueryParams}
                queryParams={queryParams}
                setIsSearchOrFilterApplied={setIsSearchOrFilterApplied}
                clearFilters={false}
                handleTabSwitch={handleTabSwitch}
                bdFilterOptions={bdFilterOptions}
            />

            {/* Table */}
            <Grid paddingX={0} paddingY={1} sx={{ position: 'relative' }}>
                {showScrollIndicator && (
                    <Box
                        sx={{
                            position: 'absolute',
                            left: `${stickyWidths.reduce((a, b) => a + b, 0)}px`,
                            top: '8px',
                            bottom: '8px',
                            width: '30px',
                            background: 'linear-gradient(to right, rgba(0,0,0,0.2), transparent)',
                            pointerEvents: 'none',
                            zIndex: 100,
                        }}
                    />
                )}
                <TableContainer
                    component={Paper}
                    ref={tableContainerRef}
                    onScroll={checkScroll}
                    sx={{
                        overflowX: 'auto',
                        maxHeight: 600,
                        position: 'relative'
                    }}
                >
                    <Table aria-label="business dashboard table" style={{ tableLayout: 'auto', borderCollapse: 'separate', borderSpacing: 0 }} stickyHeader>
                        <TableHead>
                            <TableRow>
                                {stickyHeaders.map((col, idx) => (
                                    <TableCell key={idx} style={stickyHeadStyle(idx)}>{col}</TableCell>
                                ))}
                                {scrollHeaders.map((col, idx) => (
                                    <TableCell key={idx + 6} style={{ fontSize: '14px', fontWeight: 'bold', borderBottom: '1px solid #333', position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 3 }}>{col}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tableContent}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>

            {/* Pagination */}
            {(!loading && clientGroupGroups.length > 0) && (
                <Grid container item xs={12} style={{ display: 'flex', justifyContent: 'center', paddingTop: '16px' }}>
                    <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end', paddingRight: '24px' }}>
                        <span style={{ fontFamily: '"Helvetica Neue"', fontSize: '13px', color: '#3d3d3d' }}>
                            {(() => {
                                const start = (currentPage - 1) * itemsPerPage + 1;
                                const end = (currentPage - 1) * itemsPerPage + clientGroupGroups.length;
                                const total = totalClientGroups > 0
                                    ? totalClientGroups
                                    : currentPage === totalPages
                                        ? end
                                        : totalPages * itemsPerPage;
                                return `${start}-${end} of ${total} client groups`;
                            })()}
                        </span>
                    </Grid>
                    <Pagination
                        page={currentPage}
                        count={totalPages}
                        variant="outlined"
                        shape="rounded"
                        sx={paginationStyle}
                        onChange={handlePageChange}
                    />
                </Grid>
            )}
        </>
    );
}
